import { Controller } from '@hotwired/stimulus'
import { application } from 'src';
import { addClass, find } from '@fretadao/f-js-dom';
import { isBlank } from '@fretadao/f-js-utils';

// The CSS classess starting with f-* are defined at: app/assets/stylesheets/admin/_notification_balloon.scss
// And are to this controller works on Active Admin.
application.register(
  'notifications',
  class extends Controller {
    static values = {
      title: String,
      message: String,
      iconName: String,
      duration: Number,
      textColor: String,
      bgColor: String
    };

    success() {
      this.#notify({ iconName: 'check', color: 'green' });
    }

    info() {
      this.#notify({ iconName: 'circle-info', color: 'blue' });
    }

    warn() {
      this.#notify({ iconName: 'triangle-exclamation', color: 'yellow' });
    }

    error() {
      this.#notify({ iconName: 'xmark', color: 'red' });
    }

    #notificationsContainer() {
      let container = find('#notifications-container');

      if(container) return container;

      container = document.createElement('div');
      container.id = 'notifications-container';
      addClass(container, ['fixed', 'top-0', 'right-0', 'w-full', 'sm:w-80', 'z-50']);

      find('body').append(container);

      return container;
    }

    #notify(noticeParams) {
      const options = this.#defaultNoticeOptionsFor(noticeParams);
      const noticeElem = document.createElement('div');
      addClass(
        noticeElem,
        [
          'f-notice-element',
          'max-w-md',
          'mx-auto',
          `bg-${options.bgColor}-lt`,
          'rounded-xl',
          'shadow-md',
          'm-3',
          'w-80'
        ]
      );

      const noticeBox = document.createElement('div');
      addClass(noticeBox, ['f-notice-box', 'flex', 'p-2']);

      if (options.iconName) {
        const iconBox = document.createElement('div');
        addClass(iconBox, ['f-notice-icon-box', 'flex-shrink-0', 'flex', 'items-center']);
        // TODO: We have temporarily replaced the call to the `faIcon` function with a manual implementation
        // because the `faIcon` function in the `f-js-dom` library does not yet support Font Awesome 6.
        // Once the `f-js-dom` library is updated to support Font Awesome 6,
        // we should restore the original call to the `faIcon` function to maintain code consistency.
        const icon = document.createElement('i');
        const iconClasses = ['f-notice-icon', 'fa-solid', `fa-${options.iconName}`, 'w-8', `text-${options.textColor}-dk`];
        addClass(icon, iconClasses);

        iconBox.append(icon);
        noticeBox.append(iconBox);
      }

      const messageBox = document.createElement('div');
      addClass(messageBox, 'p-2');

      if (!isBlank(options.title)) {
        const title = document.createElement('div');
        addClass(title, ['f-notice-title-box', 'uppercase', 'tracking-wide', 'text-sm', `text-${options.textColor}-dk`, 'font-semibold']);
        title.innerText = options.title;
        messageBox.append(title);
      }

      const message = document.createElement('p');
      addClass(message, ['f-notice-message', 'mt-2', 'text-gray-500']);
      message.innerText = options.message;
      messageBox.append(message);

      noticeBox.append(messageBox);
      noticeElem.append(noticeBox);

      this.#addCallbacks(noticeElem, options);

      this.#notificationsContainer().append(noticeElem);
    }

    #addCallbacks(noticeElem, options) {
      let timeout = setTimeout(() => {
        this.#removeMessage(noticeElem);
      }, options.duration);

      noticeElem.addEventListener('click', () => {
        this.#removeMessage(noticeElem);
      });

      noticeElem.addEventListener('mouseenter', () => {
        window.clearTimeout(timeout);
      });

      noticeElem.addEventListener('mouseleave', () => {
        timeout = setTimeout(() => {
          this.#removeMessage(noticeElem);
        }, options.duration);
      });
    }

    #defaultNoticeOptionsFor(noticeParams) {
      return {
        title: this.titleValue,
        message: this.messageValue,
        iconName: this.iconNameValue || noticeParams.iconName,
        duration: this.durationValue || 3000,
        textColor: this.textColorValue || noticeParams.color,
        bgColor: this.bgColorValue || noticeParams.color
      };
    }

    #removeMessage(element) {
      element.remove();
      const notificationsContainer = this.#notificationsContainer();
      if(!this.#hasChildren(notificationsContainer)) notificationsContainer.remove();
    }

    #hasChildren(element) {
      return element.childElementCount > 0;
    }
  }
);
